import PropTypes from 'prop-types';
import { NewBookingDeliveryPropType } from 'common/prop-types';
import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import usePrevious from 'common/usePrevious';
import _get from 'lodash/get';
import I18n from 'i18n-js';
import { Tooltip } from 'grab-ui';
import { newBookingOverlayType } from 'constants/new-booking-constants';
import NewBookingDuck from 'ducks/new-booking-duck';
import ScribeDuck from 'ducks/scribe-duck';
import { mergeDisplayError } from 'helper/anytime-booking';
import { enableMSDSelector } from 'selectors/app-selector';
import DeliveryStep from './delivery-step';
import styles from './styles.less';

const DeliveryRoute = ({
  form,
  deliveryIndex,
  dispatch,
  compulsoryCOD,
  delivery,
  enableMSD,
}) => {
  const deliveryDetail = _get(delivery, 'detail');
  const isRoundTrip = _get(deliveryDetail, 'isRoundTrip');
  const codAST = _get(delivery, 'config.additionalServices', []).find(
    ast => ast.id === 'CashOnDelivery',
  );
  const codFieldError = _get(codAST, 'errorMessage');
  const codMetadata = _get(codAST, 'metadata.data');
  const codDisabled = !!codFieldError || !codMetadata;
  const { hint: codDisabledHint } = mergeDisplayError(codFieldError);
  const codExponent = 10 ** _get(codMetadata, 'currency.exponent', 0);
  const codCurrencySymbol = _get(codMetadata, 'currency.symbol');
  const codLowerLimit = _get(codMetadata, 'lowerLimit', 0) / codExponent;
  const codUpperLimit = _get(codMetadata, 'upperLimit', 0) / codExponent;

  const prevCodDisabled = usePrevious(codDisabled);
  useEffect(
    () => {
      if (compulsoryCOD && codDisabled && prevCodDisabled === false) {
        dispatch(NewBookingDuck.creators.refreshDeliveryIDVersion(delivery.id));
      }
    },
    [compulsoryCOD, prevCodDisabled, codDisabled, delivery.id],
  );

  const onEdit = useCallback(
    () => {
      dispatch(
        NewBookingDuck.creators.openDetailOverlay(
          delivery.id,
          newBookingOverlayType.dropOffAddress,
        ),
      );
      dispatch(
        ScribeDuck.creators.scribeExpressDeliveryDetailEditDelivery(
          delivery.id,
        ),
      );
    },
    [dispatch, delivery.id],
  );

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>
        {I18n.t('v2.homepage.manualOrder.heading.deliveryRoute')}
      </div>
      <div className={styles.deliverySteps}>
        <DeliveryStep
          key={`${delivery.id}-p`}
          form={form}
          isPickUp
          showCODField={false}
          deliveryID={delivery.id}
          deliveryStep={deliveryDetail.pickUp}
          dispatch={dispatch}
          deliveryIndex={deliveryIndex}
        />
        {(deliveryDetail.dropOffs.length === 0
          ? [{}]
          : deliveryDetail.dropOffs
        ).map((dropOff, index) => (
          <DeliveryStep
            // eslint-disable-next-line react/no-array-index-key
            key={`${delivery.id}-d-${index}-${_get(dropOff, 'poi.id')}`}
            form={form}
            showCODField={!!codAST}
            codDisabled={codDisabled}
            codDisabledHint={codDisabledHint}
            codCurrencySymbol={codCurrencySymbol}
            codLowerLimit={codLowerLimit}
            codUpperLimit={codUpperLimit}
            compulsoryCOD={compulsoryCOD}
            deliveryID={delivery.id}
            deliveryStep={dropOff}
            dispatch={dispatch}
            showNumber={deliveryDetail.dropOffs.length > 1 || isRoundTrip}
            index={index}
            canDelete={deliveryDetail.dropOffs.length > 1}
            deliveryIndex={deliveryIndex}
          />
        ))}
        {/* add round trip step for display */}
        {isRoundTrip && (
          <DeliveryStep
            key={`${delivery.id}-rt`}
            form={form}
            isPickUp
            disabled
            isRoundTrip
            showCODField={false}
            deliveryID={delivery.id}
            deliveryStep={deliveryDetail.pickUp}
            dispatch={dispatch}
            deliveryIndex={deliveryIndex}
          />
        )}
      </div>
      {enableMSD && (
        <Tooltip
          placement="bottomLeft"
          title={I18n.t('v2.homepage.manualOrder.editDeliveryTooltip')}
        >
          <span
            className={styles.addStop}
            onClick={onEdit}
            role="button"
            tabIndex="0"
            onKeyDown={null}
          >
            {I18n.t('v2.homepage.manualOrder.button.editDelivery')}
          </span>
        </Tooltip>
      )}
    </div>
  );
};

DeliveryRoute.defaultProps = {
  compulsoryCOD: false,
};

DeliveryRoute.propTypes = {
  delivery: NewBookingDeliveryPropType.isRequired,
  form: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  compulsoryCOD: PropTypes.bool,
  deliveryIndex: PropTypes.number.isRequired,
  enableMSD: PropTypes.bool.isRequired,
};

export default connect(state => ({
  enableMSD: enableMSDSelector(state),
}))(DeliveryRoute);
